import React, { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { Configuration, GeocodingApi } from "@stadiamaps/api";
import { Autocomplete, Box, FormControl, InputLabel, MenuItem, Select, styled, TextField } from "@mui/material";
import { useDebounce } from "@/hooks/useDebounce";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { type KioLatLng, LocationPickerDefaultZoom } from "@/components/LocationPicker";

const CustomBox = styled(Box)`
  position: absolute;
  width: 80%;
  min-width: 200px;
  max-width: 600px;
  z-index: 1000;
  bottom: 12px;
  left: 10px;
`;

interface StatiaAutocompleteProps {
  position: KioLatLng | null;
  autocompleteReset: number;
  onChangeHandler: (position: KioLatLng) => void;
  setPosition: (position: KioLatLng) => void;
  mapControlRef?: React.RefObject<any>;
}
const StatiaAutocomplete: FC<StatiaAutocompleteProps> = ({
  position,
  setPosition,
  mapControlRef,
  autocompleteReset,
  onChangeHandler,
}) => {
  const stadiaConfiguration = new Configuration({ basePath: "https://api-eu.stadiamaps.com" }); // Set GDPR-compliant api endpoints
  const api = new GeocodingApi(stadiaConfiguration);
  const { t } = useTranslation("common");

  const autocompleteLocale = useMemo(() => {
    if (i18n.language === "no" || i18n.language === "nb" || i18n.language === "nn") {
      return { locale: "no", place: "NO" };
    }
    if (i18n.language === "sv" || i18n.language === "se") {
      return { locale: "se", place: "SE" };
    }
    return { locale: "en", place: "all" };
  }, [i18n]);

  const stadiaAutocompleteCountries = [
    { name: t("components.locationPickerField.norway"), code: "NO" },
    { name: t("components.locationPickerField.sweden"), code: "SE" },
    { name: t("components.locationPickerField.all"), code: "all" },
  ];

  const [places, setPlaces] = useState<any[]>([]);
  const [placesSearchValue, setPlacesSearchValue] = useState<string>("");
  const [selectedCountry, setSelectedCountry] = useState<string>(autocompleteLocale.place);

  useEffect(() => {
    if (autocompleteReset > 0) {
      setPlaces([]);
      setPlacesSearchValue("");
    }
  }, [autocompleteReset]);

  const autocompleteSearchCallback = useCallback(
    (value: string | undefined) => {
      if (!value) return;
      const country = selectedCountry;
      if (value.length > 3) {
        interface StadiaAutoCompleteParams {
          text: string;
          lang: string;
          boundaryCountry?: string[];
        }

        const autocompleteParams: StadiaAutoCompleteParams = {
          text: value,
          lang: "no",
        };
        if (country !== "all") {
          autocompleteParams["boundaryCountry"] = [country];
        }
        api.autocompleteV2(autocompleteParams).then((autocompleteRes) => {
          const gidIds = autocompleteRes?.features.map((item) => {
            return item?.properties?.gid;
          });
          api.placeDetailsV2({ ids: gidIds, lang: "no" }).then((placeDetails) => {
            setPlaces(placeDetails.features);
          });
        });
      }
    },
    [selectedCountry]
  );

  const autocompleteDebounce = useDebounce(500, autocompleteSearchCallback as (value: string) => void);

  const onInputChange = async (event: any, value: string, reason: string) => {
    setPlacesSearchValue(value);
    if (reason === "input") {
      autocompleteDebounce(value);
    } else if (reason === "clear") {
      setPlaces([]);
    }
  };

  const onSelectChange = async (event: any, value: any, reason: string) => {
    if (reason === "selectOption") {
      if (value?.position && !!setPosition) {
        const positionLatLng: KioLatLng = [value.position[1], value.position[0]];
        setPosition(positionLatLng);
        mapControlRef?.current.setView(positionLatLng, LocationPickerDefaultZoom);
        onChangeHandler(positionLatLng);
      }
    }
  };

  const onSelectCountryChange = (event: any) => {
    if (!!event.target?.value) {
      setSelectedCountry(event.target.value);
      if (placesSearchValue.length > 3) {
        autocompleteDebounce(placesSearchValue);
      }
    }
  };

  const inputOptions = places.map((option) => {
    return {
      label: `${option.properties.name}, ${option.properties.coarseLocation}`,
      key: `${option.properties.name}, ${option.properties.coarseLocation}`,
      position: option.geometry.coordinates,
    };
  });

  const onCoordinateInputChange = (evt, isLat: boolean) => {
    let latPosition = 0;
    let lngPosition = 0;

    if (isLat) {
      latPosition = Number(evt.target.value);
      if (position && position[1]) {
        lngPosition = position[1];
      }
    } else {
      lngPosition = Number(evt.target.value);
      if (position && position[0]) {
        latPosition = position[0];
      }
    }
    const positionLatLng: KioLatLng = [latPosition, lngPosition];
    !!setPosition && setPosition(positionLatLng);

    const isPositionInsideBounds = mapControlRef?.current.getBounds().contains([latPosition, lngPosition]);

    // Prevents map from centering while typing incomplete coordinates
    if (lngPosition && latPosition) {
      if (isPositionInsideBounds) {
        const positionDiff = position
          ? Math.max(Math.abs(position[0] - latPosition), Math.abs(position[1] - lngPosition))
          : 0;
        if (positionDiff > 0.09) {
          mapControlRef?.current.setView(positionLatLng);
        }
      } else {
        mapControlRef?.current.setView(positionLatLng, LocationPickerDefaultZoom);
      }

      onChangeHandler(positionLatLng);
    }

    setPlaces([]);
    setPlacesSearchValue("");
  };

  return (
    <CustomBox sx={{ display: "grid", gridAutoRows: "min-content", gap: 1 }}>
      <Box sx={{ display: "grid", gridAutoColumns: "1fr", gap: 1 }}>
        <TextField
          sx={{ gridRow: "1", gridColumn: "1/2" }}
          variant={"filled"}
          type="number"
          InputProps={{
            inputProps: {
              step: 0.0005,
              min: -90,
              max: 90,
            },
          }}
          label={t("generic.latitude")}
          value={position?.[0] || ""}
          onChange={(evt) => {
            onCoordinateInputChange(evt, true);
          }}
        ></TextField>
        <TextField
          sx={{ gridRow: "1", gridColumn: "2/3" }}
          variant={"filled"}
          type="number"
          InputProps={{
            inputProps: {
              step: 0.001,
              min: -180,
              max: 180,
            },
          }}
          label={t("generic.longitude")}
          value={position?.[1] || ""}
          onChange={(evt) => {
            onCoordinateInputChange(evt, false);
          }}
        ></TextField>
      </Box>
      <Box sx={{ display: "grid", gridAutoColumns: "1fr", gap: 1 }}>
        <Autocomplete
          sx={{ gridRow: "1", gridColumn: "1/4" }}
          fullWidth
          id="free-solo-demo"
          freeSolo
          options={inputOptions}
          onInputChange={onInputChange}
          inputValue={placesSearchValue}
          onChange={onSelectChange}
          renderInput={(params) => (
            <TextField variant="filled" {...params} label={t("components.locationPickerField.search")} />
          )}
        />
        <FormControl sx={{ gridRow: "1", gridColumn: "4/5" }} fullWidth>
          <InputLabel variant={"filled"} id={"stadia-autocomplete-country-select-label"}>
            {t("components.locationPickerField.country")}
          </InputLabel>
          <Select
            variant="filled"
            id={"stadia-autocomplete-country-select"}
            labelId={"stadia-autocomplete-country-select-label"}
            defaultValue={autocompleteLocale.place}
            onChange={onSelectCountryChange}
          >
            {stadiaAutocompleteCountries.map((countryOption, idx) => (
              <MenuItem key={`stadiaAutocompleteCountryOption_${idx}`} value={countryOption.code}>
                {countryOption.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </CustomBox>
  );
};
export default StatiaAutocomplete;
